<script setup>
import { computed } from 'vue';
import Draggable from 'vuedraggable';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';
import KanbanCard from './KanbanCard.vue';

const props = defineProps({
  stage: { type: Object, required: true },
  cards: { type: Array, default: () => [] },
  totalCount: { type: Number, default: 0 },
  showValue: { type: Boolean, default: false },
  currency: { type: String, default: 'BRL' },
  canManage: { type: Boolean, default: false },
  // CRM-01h fase 3: de onde o "Carregar mais" continua; nulo quando a coluna veio inteira.
  nextCursor: { type: Object, default: null },
  loadingMore: { type: Boolean, default: false },
  // Seleção do "mover em bloco".
  selectionActive: { type: Boolean, default: false },
  stageSelected: { type: Boolean, default: false },
  isCardSelected: { type: Function, default: () => false },
});

const emit = defineEmits([
  'drop',
  'editStage',
  'openCard',
  'toggleCard',
  'toggleStage',
  'loadMore',
]);

const cardList = computed({
  get: () => props.cards,
  // vuedraggable needs a writable model; the store already mutated the array in place
  // during the optimistic move, so there is nothing to do here.
  set: () => {},
});

const isOverLimit = computed(
  () => props.stage.wip_limit && props.totalCount > props.stage.wip_limit
);

const totalValue = computed(() =>
  props.cards.reduce((sum, card) => sum + (card.value_cents || 0), 0)
);

const formattedTotal = computed(() =>
  new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: props.currency || 'BRL',
  }).format(totalValue.value / 100)
);

// vuedraggable gives us the destination index; the store turns that into neighbour ids.
const onChange = event => {
  const payload = event.added || event.moved;
  if (!payload) return;
  emit('drop', {
    cardId: payload.element.id,
    fromStageId: payload.element.stage_id,
    toStageId: props.stage.id,
    newIndex: payload.newIndex,
  });
};

// Cards leaving this column are handled by the destination's `added` event.
const onRemove = () => {};
</script>

<template>
  <section
    class="flex flex-col flex-shrink-0 w-[300px] max-h-full rounded-lg bg-n-alpha-1"
  >
    <header class="flex items-center gap-2 px-3 py-2.5 group/header">
      <!-- CRM-01h fase 3: "Selecionar os N desta etapa", inclusive os que a coluna ainda não
           mostrou — o servidor resolve pelos filtros. Aparece ao passar o mouse no cabeçalho e
           fica à vista enquanto houver seleção. -->
      <span
        v-if="totalCount"
        class="items-center flex-shrink-0 hidden group-hover/header:flex"
        :class="{ '!flex': selectionActive || stageSelected }"
        :title="$t('KANBAN.COLUMN.SELECT_ALL', { count: totalCount })"
      >
        <Checkbox
          :model-value="stageSelected"
          :aria-label="$t('KANBAN.COLUMN.SELECT_ALL', { count: totalCount })"
          @change="emit('toggleStage', stage.id)"
        />
      </span>
      <!-- Trello-style: the column only drags from this grip, so grabbing anywhere in
           the header (or a card) never picks up the whole stage by accident. -->
      <button
        v-if="canManage"
        class="kanban-stage-handle flex items-center justify-center flex-shrink-0 rounded cursor-grab active:cursor-grabbing size-6 text-n-slate-11 bg-n-alpha-2 hover:bg-n-alpha-3 hover:text-n-slate-12"
        :title="$t('KANBAN.COLUMN.DRAG_HANDLE')"
        :aria-label="$t('KANBAN.COLUMN.DRAG_HANDLE')"
      >
        <Icon icon="i-lucide-grip-vertical" class="size-4" />
      </button>
      <span
        class="flex-shrink-0 rounded-full size-2.5"
        :style="{ backgroundColor: stage.color }"
      />
      <h3 class="text-sm font-medium truncate text-n-slate-12">
        {{ stage.name }}
      </h3>
      <span
        class="px-1.5 py-0.5 text-xs rounded tabular-nums"
        :class="
          isOverLimit
            ? 'bg-n-ruby-3 text-n-ruby-11'
            : 'bg-n-alpha-2 text-n-slate-11'
        "
        :title="
          stage.wip_limit
            ? $t('KANBAN.COLUMN.WIP_LIMIT', { limit: stage.wip_limit })
            : undefined
        "
      >
        {{ totalCount
        }}<template v-if="stage.wip_limit">/{{ stage.wip_limit }}</template>
      </span>
      <span class="flex-1" />
      <span
        v-if="showValue && totalValue"
        class="text-xs font-medium text-n-teal-11 tabular-nums"
      >
        {{ formattedTotal }}
      </span>
      <button
        v-if="canManage"
        class="flex items-center justify-center rounded size-6 text-n-slate-11 hover:bg-n-alpha-2"
        :title="$t('KANBAN.COLUMN.EDIT')"
        :aria-label="$t('KANBAN.COLUMN.EDIT')"
        @click="emit('editStage', stage)"
      >
        <Icon icon="i-lucide-settings-2" class="size-3.5" />
      </button>
    </header>

    <Draggable
      v-model="cardList"
      group="kanban-cards"
      item-key="id"
      class="flex flex-col gap-2 px-2 pb-2 overflow-y-auto min-h-[80px] flex-1 pz-scrollbar"
      ghost-class="opacity-40"
      drag-class="rotate-1"
      @change="onChange"
      @remove="onRemove"
    >
      <template #item="{ element }">
        <KanbanCard
          :card="element"
          :show-value="showValue"
          :currency="currency"
          :selected="isCardSelected(element)"
          :selection-active="selectionActive"
          :selection-locked="stageSelected"
          @open="emit('openCard', $event)"
          @toggle-select="emit('toggleCard', $event)"
        />
      </template>
    </Draggable>

    <div
      v-if="cards.length < totalCount"
      class="flex flex-col items-center gap-1 px-3 py-2"
    >
      <span class="text-xs text-n-slate-10 tabular-nums">
        {{
          $t('KANBAN.COLUMN.SHOWING', {
            shown: cards.length,
            total: totalCount,
          })
        }}
      </span>
      <button
        v-if="nextCursor"
        type="button"
        class="text-xs font-medium text-n-brand hover:underline disabled:opacity-50"
        :disabled="loadingMore"
        @click="emit('loadMore', stage.id)"
      >
        {{
          loadingMore
            ? $t('KANBAN.COLUMN.LOADING_MORE')
            : $t('KANBAN.COLUMN.LOAD_MORE', {
                count: totalCount - cards.length,
              })
        }}
      </button>
    </div>
  </section>
</template>
